import { useEventCallback } from '@cian/react-utils';
import { useDeviceType } from '@cian/ui-kit/responsive';
import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { GEO_CHANGE_ACTIONS } from './constants';
import { trackAdvancedFiltersClick, trackClearClick, trackFilterFromChipsApply } from './tracking';
import { useLastChangedFilterType } from './utils';
import { IOnChangeParameters, Provider } from '../../../../packages/Filters';
import { NonEmptyArray } from '../../../../packages/JsonQuery';
import { IJsonQuery } from '../../../../packages/api-models/common/json_query';
import { trackListingShow } from '../../../ListingMobile';
import { fetchMapData } from '../../../Map/actions';
import { trackMapShow } from '../../../Map/tracking/trackMapShow';
import { changeJsonQuery, fetchTags } from '../../actions/filters';
import { EListingModalMode, EOffersFetchingMode } from '../../actions/listingModal';
import { loadListingOffers, removeOffers } from '../../actions/listingOffers';
import { fetchRegion } from '../../actions/region';
import { selectIsModalOpen, selectModalMode } from '../../selectors/listingModal';
import { IApplicationState, TThunkDispatch } from '../../types/redux';
import { IRegionSpecialPromo } from '../../types/specialPromos';
import { produceGeoChangeEvent } from '../../utils/events';
import { normalizeRegionId } from '../../utils/region';
import { getAvailableSpecialPromo } from '../../utils/specialPromos';
import { usePrevious } from '../../utils/usePrevious';
import { useApplicationContext } from '../ApplicationContext';

interface IFiltersContainerProps {
  children: React.ReactChild | React.ReactChild[];
}

export const FiltersContainer: React.FC<IFiltersContainerProps> = ({ children }) => {
  const { microfrontendsAPI, config } = useApplicationContext();
  const deviceType = useDeviceType();
  const dispatch = useDispatch<TThunkDispatch>();
  const jsonQuery = useSelector<IApplicationState, IJsonQuery>(state => state.filters.jsonQuery);
  const specialPromos = useSelector<IApplicationState, IRegionSpecialPromo[]>(state => state.specialPromos);
  const queryString = useSelector<IApplicationState, string>(state => state.queryString);
  const [isAwaitingTracking, setIsAwaitingTracking] = React.useState<boolean>(false);
  const isAwaitingGeoUpdate = React.useRef<boolean>(false);
  const specialPromo: IRegionSpecialPromo | undefined = getAvailableSpecialPromo(specialPromos, jsonQuery);
  const isListingModalOpen = useSelector(selectIsModalOpen);
  const prevIsListingModalOpen = usePrevious(isListingModalOpen);
  const listingModalMode = useSelector(selectModalMode);

  const { lastChangedFilterType, setLastAppliedModifiers } = useLastChangedFilterType();

  const experimentalFeatures = React.useMemo(() => ({}), []);

  const handleChange = useEventCallback((parameters: IOnChangeParameters) => {
    const nextJsonQuery = dispatch(changeJsonQuery({ ...parameters, prevJsonQuery: jsonQuery }));

    setLastAppliedModifiers(parameters.appliedModifiers);

    if (parameters.appliedModifiers.some(modifier => GEO_CHANGE_ACTIONS.includes(modifier.action))) {
      isAwaitingGeoUpdate.current = true;
      dispatch(fetchTags());
      dispatch(fetchRegion());

      if (config.get<boolean>('header.asMicrofrontend')) {
        const setRegionModifier = parameters.appliedModifiers.find(modifier => modifier.action === 'setRegion');

        if (setRegionModifier !== undefined && microfrontendsAPI) {
          const regionId = normalizeRegionId(setRegionModifier.arguments[0] as NonEmptyArray<number> | null);

          if (regionId) {
            microfrontendsAPI.header.setRegionId({ regionId });
          }
        }
      } else {
        produceGeoChangeEvent(parameters.appliedModifiers);
      }
    }

    return nextJsonQuery;
  });

  const handleApply = useEventCallback(() => {
    dispatch(fetchTags());
    dispatch(fetchMapData({ updateBounds: isAwaitingGeoUpdate.current, deviceType }));
    dispatch(removeOffers());
    dispatch(loadListingOffers({ offersFetchingMode: EOffersFetchingMode.ALL }));
    isAwaitingGeoUpdate.current = false;
  });

  const handleApplyClick = React.useCallback(() => {
    setIsAwaitingTracking(true);
  }, []);

  React.useEffect(() => {
    if (!isAwaitingTracking) {
      return;
    }

    trackFilterFromChipsApply({
      queryString,
      jsonQuery,
      pageType: isListingModalOpen ? 'Listing' : 'Map',
      filterType: lastChangedFilterType,
    });

    // если открыта модалка листинга, очищаем её, загружаем новые объявки
    // проверка на deviceType нужна, потому что на десктопе не нужно трекать событие и
    // подгружать листинг
    if (isListingModalOpen && !prevIsListingModalOpen && deviceType === 'phone') {
      dispatch(removeOffers());
      dispatch(loadListingOffers({})).then(res => {
        // трекаем событие newpage листинга, если модалка открыта на весь экран
        if (listingModalMode === EListingModalMode.FULL_SCREEN) {
          trackListingShow('all_objects_on_map')(res);
        }
      });
    } else {
      // трекаем событие newpage карты
      trackMapShow({ jsonQuery });
    }

    setIsAwaitingTracking(false);
  }, [dispatch, isListingModalOpen, prevIsListingModalOpen, queryString]);

  return (
    <Provider
      jsonQuery={jsonQuery}
      specialPromo={specialPromo}
      onChange={handleChange}
      onApply={handleApply}
      onClear={trackClearClick}
      onApplyClick={handleApplyClick}
      onAdvancedFiltersOpen={trackAdvancedFiltersClick}
      features={experimentalFeatures}
    >
      {children}
    </Provider>
  );
};
